import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { Database, ExternalLink, ArrowRight } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: "Données ADEME — Source officielle des informations RGE | AfficheServices" },
  { name: 'description', content: "ADEME (Agence de la transition écologique) : source officielle des données sur les artisans RGE, les aides à la rénovation énergétique et les bilans environnementaux." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/ademe' },
]

const DATASETS = [
  {
    name: 'Liste des entreprises RGE',
    url: 'https://data.ademe.fr/datasets/liste-des-entreprises-rge-2',
    description: "Annuaire officiel des artisans RGE avec SIRET, qualification, date de validité, département.",
    license: 'Licence Etalab 2.0',
  },
  {
    name: 'MaPrimeRénov\' — demandes agrégées',
    url: 'https://data.ademe.fr/datasets/maprimerenov',
    description: "Statistiques publiques sur les demandes MaPrimeRénov' : nombre, montants, types de travaux, régions.",
    license: 'Licence Etalab 2.0',
  },
  {
    name: 'DPE — Base nationale',
    url: 'https://data.ademe.fr/datasets/dpe-v2-logements-existants',
    description: 'Diagnostics de performance énergétique réalisés sur le parc immobilier français existant.',
    license: 'Licence Etalab 2.0',
  },
  {
    name: 'CEE — Fiches d\'opérations standardisées',
    url: 'https://atee.fr/efficacite-energetique/club-c2e',
    description: "Catalogue technique des opérations CEE avec formules de calcul des kWh cumac.",
    license: 'Consultation libre',
  },
]

export default function ADEMEPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Données ADEME', url: 'https://www.afficheservices.fr/ademe' },
      ]} />

      <section className="bg-gradient-to-br from-teal-900 via-emerald-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200">
            <Database className="h-3.5 w-3.5" /> Source officielle
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">Données ADEME</h1>
          <p className="mt-3 max-w-2xl text-base text-emerald-200">
            L'Agence de la transition écologique (ADEME) publie en open data l'ensemble des informations
            officielles sur les artisans RGE, les aides à la rénovation et les bilans environnementaux.
            Toutes nos pages s'appuient sur ces données.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <section>
          <h2 className="text-xl font-bold text-slate-900">Qu'est-ce que l'ADEME ?</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            L'ADEME (Agence de la transition écologique) est un établissement public de l'État français sous la tutelle
            conjointe des ministères de la Transition écologique et de l'Enseignement supérieur. Elle met en œuvre les politiques
            publiques dans les domaines de l'environnement, de l'énergie et du développement durable. Elle gère notamment
            France Rénov', l'annuaire RGE, et publie les données ouvertes du secteur.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">Jeux de données utilisés</h2>
          <p className="mt-2 text-sm text-slate-500">
            Les principaux datasets ADEME exploités pour alimenter nos pages sectorielles.
          </p>
          <div className="mt-5 space-y-3">
            {DATASETS.map((d) => (
              <a
                key={d.url}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="group flex items-start justify-between gap-4 rounded-xl border border-border bg-white p-5 shadow-xs transition-all hover:border-primary hover:shadow-md"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 group-hover:text-primary">
                    {d.name} <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                  </h3>
                  <p className="mt-1 text-xs text-slate-600">{d.description}</p>
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    {d.license}
                  </p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 group-hover:text-primary" />
              </a>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-border bg-slate-50 p-6">
          <h2 className="text-lg font-bold text-slate-900">Licence Etalab 2.0</h2>
          <p className="mt-2 text-sm text-slate-600">
            Les données publiées par l'ADEME sont diffusées sous la{' '}
            <a
              href="https://www.etalab.gouv.fr/licence-ouverte-open-licence/"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-primary hover:underline"
            >
              Licence Ouverte 2.0 (Etalab)
            </a>
            , qui autorise leur réutilisation libre et gratuite, y compris commerciale, à condition de mentionner
            la source et la date de mise à jour. AfficheServices respecte cette obligation de transparence.
          </p>
        </section>

        <section className="mt-8 text-center">
          <Link
            to="/rge/sources"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            Méthodologie AfficheServices <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
